/**
 * ListenerCard — Smarter You listener progression on the Profile page.
 *
 * Shows:
 *   • Evolution + level (e.g. "Sapling · Lv. 4 / 10") + XP progress bar.
 *   • Lifetime / weekly / today minutes.
 *   • A compact evolution ladder so the user can see the whole journey.
 *   • By-type breakdown (audiobook / cinematic / TTS).
 *   • XP-per-100 speed table so the user knows how to speed up earning.
 *   • Reward-on-promotion: +10 coins per evolution (100 coins lifetime).
 *
 * Speed table + ladder come straight from /api/listening/stats so the
 * UI stays in sync with the backend formula even if the table moves.
 */
import React, { useEffect, useState } from 'react';
import { Headphones, Gauge, Sparkles, ChevronRight } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { authFetch } from '../utils/authUtils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const formatMinutes = (n) => {
  const m = Number(n) || 0;
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  const mins = m % 60;
  if (h < 24) return `${h}h ${mins}m`;
  const days = Math.floor(h / 24);
  return `${days}d ${h % 24}h`;
};

const ListenerCard = () => {
  const [data, setData] = useState(null);
  const [ladderOpen, setLadderOpen] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await authFetch(`${BACKEND_URL}/api/listening/stats`);
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const d = await r.json();
        if (alive) setData(d);
      } catch {
        if (alive) setData({});
      }
    })();
    return () => { alive = false; };
  }, []);

  if (!data) return null;
  const lifetime = data.lifetime_minutes || 0;
  const weekly = data.weekly_minutes || 0;
  const today = data.today_minutes || 0;
  const xp = data.lifetime_xp_from_listening || 0;
  const coins = data.lifetime_coins_from_listening || 0;
  const byType = data.by_type || {};
  const prog = data.progression || {};
  const ladder = data.ladder || [];
  const speedTable = data.speed_table || [];
  const pct = prog.xp_per_level
    ? Math.round(((prog.xp_into_level || 0) / prog.xp_per_level) * 100)
    : 0;
  const minsTo1x = data.mins_to_next_level_at_1x || 0;
  const minsTo2x = data.mins_to_next_level_at_2x || 0;

  return (
    <Card
      className="bg-gradient-to-br from-fuchsia-900/40 to-indigo-900/40 border border-fuchsia-500/30 md:col-span-2"
      data-testid="profile-listener-card"
    >
      <CardContent className="p-5 sm:p-6">
        <div className="flex items-center justify-between gap-3 mb-4">
          <div className="flex items-center gap-2">
            <Headphones className="w-5 h-5 text-fuchsia-300" />
            <h3 className="text-base sm:text-lg font-black text-white">Smarter You · Listener</h3>
          </div>
          <span
            className="text-[10px] uppercase tracking-widest font-bold px-2 py-1 rounded-full border border-fuchsia-400/50 text-fuchsia-200 bg-fuchsia-500/15"
            data-testid="profile-listener-tier"
          >
            {prog.evolution_name || 'Seedling'} · Lv. {prog.level_in_evolution || 1}/10
          </span>
        </div>

        {/* Level XP bar */}
        <div className="mb-4">
          <div className="flex justify-between text-[11px] mb-1.5">
            <span className="text-fuchsia-200 font-bold inline-flex items-center gap-1">
              <Sparkles className="w-3 h-3" /> Next level
            </span>
            <span className="tabular-nums text-slate-300">
              {prog.xp_into_level || 0}/{prog.xp_per_level || 100} XP
            </span>
          </div>
          <div className="w-full h-2.5 rounded-full bg-slate-700/60 overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-fuchsia-500 to-amber-400 transition-all duration-700"
              style={{ width: `${pct}%` }}
              data-testid="profile-listener-level-bar"
            />
          </div>
          <p className="text-[10px] text-slate-400 mt-1.5">
            +{prog.xp_to_next_level || 0} XP to next level (~{minsTo1x} min @ 1× · {minsTo2x} min @ 2×) ·
            +{prog.xp_to_next_evolution || 0} XP to <strong className="text-fuchsia-200">
              {(ladder[(prog.evolution_index || 0) + 1] || {}).name || 'max'}
            </strong>
          </p>
        </div>

        {/* Three quick stats */}
        <div className="grid grid-cols-3 gap-2 mb-4">
          <div className="rounded-lg bg-slate-900/40 border border-white/5 p-3 text-center">
            <p className="text-[10px] uppercase tracking-wider text-slate-400 font-bold">Today</p>
            <p className="text-base sm:text-lg font-black text-white tabular-nums">{formatMinutes(today)}</p>
          </div>
          <div className="rounded-lg bg-slate-900/40 border border-white/5 p-3 text-center">
            <p className="text-[10px] uppercase tracking-wider text-slate-400 font-bold">This week</p>
            <p className="text-base sm:text-lg font-black text-white tabular-nums" data-testid="profile-listener-weekly">
              {formatMinutes(weekly)}
            </p>
          </div>
          <div className="rounded-lg bg-slate-900/40 border border-white/5 p-3 text-center">
            <p className="text-[10px] uppercase tracking-wider text-slate-400 font-bold">Lifetime</p>
            <p className="text-base sm:text-lg font-black text-white tabular-nums" data-testid="profile-listener-lifetime">
              {formatMinutes(lifetime)}
            </p>
          </div>
        </div>

        {/* Evolution ladder — collapsed by default, opens to a chip row. */}
        <button
          type="button"
          onClick={() => setLadderOpen((o) => !o)}
          className="w-full text-left flex items-center justify-between text-[11px] uppercase tracking-widest font-bold text-fuchsia-200 hover:text-fuchsia-100 transition-colors mb-2"
          data-testid="profile-listener-ladder-toggle"
        >
          <span>Evolution ladder</span>
          <ChevronRight className={`w-3.5 h-3.5 transition-transform ${ladderOpen ? 'rotate-90' : ''}`} />
        </button>
        {ladderOpen && (
          <div
            className="flex flex-wrap gap-1.5 mb-4 p-2 rounded-lg bg-slate-900/40 border border-white/5"
            data-testid="profile-listener-ladder"
          >
            {ladder.map((step) => {
              const isCurrent = step.index === (prog.evolution_index || 0);
              const isPast = step.completed;
              return (
                <span
                  key={step.key}
                  className={`px-2 py-1 rounded-full text-[10px] font-black uppercase tracking-wider border ${
                    isPast
                      ? 'bg-emerald-500/20 border-emerald-400/50 text-emerald-200'
                      : isCurrent
                      ? 'bg-fuchsia-500/30 border-fuchsia-400 text-white shadow-md shadow-fuchsia-500/40'
                      : 'bg-slate-800/60 border-slate-700 text-slate-500'
                  }`}
                  title={`${step.cumulative_xp_at_finish.toLocaleString()} XP total`}
                >
                  {step.name}
                </span>
              );
            })}
          </div>
        )}

        {/* Speed table — quick reference so user knows how to earn faster */}
        {speedTable.length > 0 && (
          <div className="mb-4 p-3 rounded-lg bg-slate-900/40 border border-white/5" data-testid="profile-listener-speed-table">
            <p className="text-[10px] uppercase tracking-widest font-bold text-amber-200 mb-2 inline-flex items-center gap-1">
              <Gauge className="w-3 h-3" /> Mins per 100 XP
            </p>
            <div className="grid grid-cols-6 gap-1.5">
              {speedTable.map((row) => (
                <div key={row.speed} className="text-center">
                  <p className="text-[10px] text-slate-400 font-bold tabular-nums">{row.speed}×</p>
                  <p className="text-sm text-white font-black tabular-nums">{row.minutes_per_100_xp}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Breakdown + lifetime rewards */}
        <div className="flex items-center justify-between text-[11px] text-slate-300 pt-3 border-t border-white/10">
          <span data-testid="profile-listener-breakdown">
            📚 {formatMinutes(byType.audiobook_minutes || 0)} ·
            🎬 {formatMinutes(byType.cinematic_minutes || 0)} ·
            🔊 {formatMinutes(byType.tts_minutes || 0)}
          </span>
          <span className="font-bold tabular-nums text-amber-300">
            +{xp.toLocaleString()} XP · +{coins} 🪙
          </span>
        </div>
      </CardContent>
    </Card>
  );
};

export default ListenerCard;
